import { useCallback, useEffect, useRef } from "react";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";

/**
 * Bar-owner product tour (Driver.js).
 *
 * Anchors to elements in the bar-owner navigation that are tagged with
 * `data-tour="..."`. Auto-starts once per browser for owners with at least one
 * bar, and can be re-run on demand via the returned `startTour` callback.
 */

export const BAR_OWNER_TOUR_STORAGE_KEY = "bhpr.barOwnerTourCompleted.v1";

const TOUR_STEPS = [
    {
        element: "[data-tour=\"bar-select\"]",
        popover: {
            title: "Switch between your bars",
            description:
                "Manage more than one bar? Pick the one you want to work on here \u2014 everything below updates to match."
        }
    },
    {
        element: "[data-tour=\"profile\"]",
        popover: {
            title: "Your bar profile",
            description:
                "Keep your hours, photos, categories, and details current. This is what barhoppers see when they find you."
        }
    },
    {
        element: "[data-tour=\"managers\"]",
        popover: {
            title: "Add your team",
            description:
                "Invite managers so your whole team can help keep your listing and promotions up to date."
        }
    },
    {
        element: "[data-tour=\"specials\"]",
        popover: {
            title: "List your specials",
            description:
                "Add your happy hours and drink specials, and upload promotional posters or flyers, so they show up for people deciding where to go out."
        }
    },
    {
        element: "[data-tour=\"promotions\"]",
        popover: {
            title: "Run live promotions",
            description:
                "Push a promotion straight to barhoppers near you, right when it matters most."
        }
    },
    {
        element: "[data-tour=\"placements\"]",
        popover: {
            title: "Get featured",
            description:
                "Boost your bar to the top of the categories your ideal crowd is browsing with featured placements."
        }
    },
    {
        popover: {
            title: "Need a refresher?",
            description:
                "Each screen has its own quick tour. Click the help (?) icon in the top bar to replay the tour for the screen you're on anytime."
        }
    }
];

function markTourComplete() {
    try {
        window.localStorage.setItem(BAR_OWNER_TOUR_STORAGE_KEY, "1");
    } catch {
        // Storage may be unavailable (private mode); failing to persist just
        // means the tour can auto-start again, which is acceptable.
    }
}

function hasCompletedTour() {
    try {
        return window.localStorage.getItem(BAR_OWNER_TOUR_STORAGE_KEY) === "1";
    } catch {
        return false;
    }
}

/**
 * Starts the tour, skipping any steps whose target element isn't on the page.
 */
export function startBarOwnerTour(onDone) {
    const steps = TOUR_STEPS.filter(
        (step) => !step.element || document.querySelector(step.element)
    );
    if (steps.length === 0) return;

    const tour = driver({
        showProgress: true,
        allowClose: true,
        nextBtnText: "Next",
        prevBtnText: "Back",
        doneBtnText: "Got it",
        steps,
        onDestroyed: () => {
            markTourComplete();
            if (typeof onDone === "function") onDone();
        }
    });

    tour.drive();
}

/**
 * Auto-starts the bar-owner tour once (per browser) when `enabled` becomes
 * true, and returns a `startTour` callback for manual re-runs.
 */
export function useBarOwnerTour(enabled) {
    const autoStartedRef = useRef(false);

    const startTour = useCallback(() => {
        startBarOwnerTour();
    }, []);

    useEffect(() => {
        // Auto-starts once per browser for owners with at least one bar.
        if (!enabled || autoStartedRef.current || hasCompletedTour()) return undefined;

        // Defer so the nav has rendered and the anchor elements exist.
        const timer = setTimeout(() => {
            if (document.querySelector("[data-tour=\"profile\"]")) {
                autoStartedRef.current = true;
                startBarOwnerTour();
            }
        }, 900);

        return () => clearTimeout(timer);
    }, [enabled]);

    return { startTour };
}
